import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE, lineaAnotacion } from './chartBase';
import { COLOR, FUENTE, colorPorUmbral, UMBRALES } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_ESPERA_SALA } from './mockDataEjemplos';

/**
 * Espera en sala por franja del turno. Los minutos son los del backend: llegada real → inicio de la
 * atención, solo con llegada e inicio del mismo día y en ese orden (es la misma definición que la
 * "Espera media" del encabezado, que promedia por turno). Por eso este gráfico ya no calcula su propio
 * promedio: el general es el del encabezado (`esperaGeneral`) y, si no viene, se pondera por los turnos
 * medidos de cada franja en vez de promediar los promedios.
 *
 * El eje se recorta en `escalaMax`: una franja con una espera enorme se dibuja al tope con una nota y
 * su valor exacto, en vez de estirar el eje y aplastar las demás.
 * @param {{data: Array<{horario: string, tiempoEsperaPromedioMinutos: number|null, turnosMedidos?: number}>,
 *   esperaGeneral?: number|null, escalaMax?: number}} props
 */
const EsperaEnSala = forwardRef(({ data, esperaGeneral = null, escalaMax = 60, compacto = false }, ref) => {
    const filas = useMemo(() => (data || []).filter((f) => f.tiempoEsperaPromedioMinutos != null), [data]);

    const outliers = useMemo(
        () => filas.filter((f) => Number(f.tiempoEsperaPromedioMinutos) > escalaMax),
        [filas, escalaMax]
    );

    const esperaPromedio = useMemo(() => {
        if (esperaGeneral != null) return Number(esperaGeneral);
        if (!filas.length) return null;
        const medidos = filas.reduce((acc, f) => acc + (f.turnosMedidos || 0), 0);
        if (medidos > 0) {
            return filas.reduce((acc, f) => acc + Number(f.tiempoEsperaPromedioMinutos) * (f.turnosMedidos || 0), 0) / medidos;
        }
        return filas.reduce((acc, f) => acc + Number(f.tiempoEsperaPromedioMinutos), 0) / filas.length;
    }, [filas, esperaGeneral]);

    const chartData = {
        labels: filas.map((f) => f.horario),
        datasets: [{
            data: filas.map((f) => Math.min(Number(f.tiempoEsperaPromedioMinutos), escalaMax)),
            backgroundColor: filas.map((f) => colorPorUmbral(Number(f.tiempoEsperaPromedioMinutos), UMBRALES.esperaMin, true)),
            borderRadius: 4,
            barThickness: 18,
        }],
    };

    const annotations = {
        meta: lineaAnotacion(UMBRALES.esperaMin.bueno, COLOR.verde, `meta ${UMBRALES.esperaMin.bueno} min`),
    };
    outliers.forEach((f) => {
        annotations[`outlier-${f.horario}`] = {
            type: 'label',
            xValue: f.horario,
            yValue: escalaMax,
            yAdjust: -12,
            content: `${Math.round(Number(f.tiempoEsperaPromedioMinutos))}↑`,
            color: COLOR.rojo,
            font: { family: FUENTE, size: 11, weight: '700' },
            backgroundColor: 'transparent',
        };
    });

    const options = {
        ...BASE_OPTIONS,
        plugins: {
            ...BASE_OPTIONS.plugins,
            legend: { display: false },
            annotation: { annotations },
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: {
                    label: (ctx) => {
                        const fila = filas[ctx.dataIndex];
                        const real = Number(fila.tiempoEsperaPromedioMinutos);
                        const n = fila.turnosMedidos ? ` · ${fila.turnosMedidos} turnos` : '';
                        return real > escalaMax ? `${real.toFixed(0)} min (fuera de escala)${n}` : `${real.toFixed(0)} min${n}`;
                    },
                },
            },
        },
        scales: {
            x: { ...EJE_X_BASE },
            y: { ...EJE_Y_BASE, min: 0, max: escalaMax, title: { display: true, text: 'min', color: COLOR.grisTexto, font: { family: FUENTE, size: 11 } } },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Espera en sala"
                        queMuestra="Te dice cuánto esperó realmente la gente en sala antes de ser atendida, franja por franja, y qué tan lejos está eso de la meta del estudio."
                        ejemploVisual={<EsperaEnSala compacto data={MOCK_ESPERA_SALA} />}
                        comoLeerlo={[
                            'Cada barra es una franja horaria del turno; su altura es el minuto de espera promedio de esa franja.',
                            'La espera es el tiempo entre que la persona llegó (hora de llegada registrada) y empezó su atención. Solo cuentan los turnos con llegada registrada, con llegada e inicio del mismo día y en ese orden: es la misma definición de la "Espera media" del encabezado.',
                            'Verde = espera ≤15 min (la meta). Ámbar = entre 15 y 30 min. Rojo = más de 30 min.',
                            'La línea verde punteada marca la meta de 15 min.',
                            'El eje llega a 60 minutos a propósito: una franja con más espera se dibuja pegada al borde con una etiqueta roja (↑) y el minuto exacto, en vez de estirar todo el eje y aplastar las demás. Debajo del gráfico se listan esas franjas.',
                            'El promedio general que se lee debajo es el del encabezado: promedia por turno, no por franja, así que una franja con pocos turnos no pesa lo mismo que una con muchos.',
                        ]}
                        analisis="En este ejemplo casi todas las franjas están cerca de la meta, salvo 08:45 (35 min) y 09:45, que llega a 75 min y se dibuja al tope con su valor exacto. Una sola franja mala no arruina el día: conviene mirar qué pasó puntualmente en ese horario (una atención larga que retrasó a toda la cola, un operador ausente) antes de tocar la dotación de todo el día."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Espera en sala</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Minutos entre que la persona llegó y empezó su atención, por franja del turno (solo llegadas registradas del mismo día).
                    </Typography>
                </>
            )}
            <Box sx={{ height: compacto ? 170 : 240 }}>
                <Bar ref={ref} data={chartData} options={options} />
            </Box>
            {!compacto && esperaPromedio !== null && (
                <Box sx={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {outliers.length > 0 && (
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto }}>
                            {outliers.length} franja(s) por encima de {escalaMax} min, dibujadas al tope con su valor exacto:{' '}
                            {outliers.map((f) => `${f.horario} (${Number(f.tiempoEsperaPromedioMinutos).toFixed(0)} min)`).join(' · ')}.
                        </Typography>
                    )}
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 11.5, color: COLOR.grisTexto }}>
                        Espera media del período: <b>{esperaPromedio.toFixed(0)} min</b>
                        {esperaGeneral != null ? ' (la del encabezado, promedio por turno)' : ' (ponderada por turnos medidos)'}.{' '}
                        {esperaPromedio > UMBRALES.esperaMin.alerta
                            ? 'La espera está lejos de la meta de forma sostenida — conviene revisar dotación en las franjas más cargadas.'
                            : esperaPromedio > UMBRALES.esperaMin.bueno
                                ? 'La espera está apenas por encima de la meta — vigilar si se mantiene.'
                                : 'La espera está dentro de la meta.'}
                    </Typography>
                </Box>
            )}
        </Card>
    );
});

export default EsperaEnSala;
